<script setup>
import { nextTick, onMounted, ref, watch } from 'vue';
import { fetchData } from '../../fetchData';
import HighlightItem from './HighlightItem.vue';
import HighlightItemSkeleton from './HighlightItemSkeleton.vue';

const props = defineProps({
    limit: {
        type: Number,
        default: 5
    },
    highlighted: {
        type: Boolean,
        default: undefined
    },
    post_tags: {
        type: Array,
        default: new Array()
    },
    except: {
        type: Array,
        default: new Array()
    }
});

const posts = ref([]);
const loading = ref(true);
const error = ref(null);
const sliderContainer = ref(null);
const isAtStart = ref(true);
const isAtEnd = ref(false);

const fetchPosts = async () => {
    error.value = null;

    const queryParams = new URLSearchParams({
        limit: props.limit,
        'sort[by]': 'created_at',
        'sort[order]': 'desc'
    });

    if (props.highlighted) {
        queryParams.append('highlighted', true);
    }

    if (props.post_tags.length >= 0) {
        props.post_tags.forEach((tag) => {
            queryParams.append('post_tags[]', tag);
        });
    }

    if (props.except.length >= 0) {
        props.except.forEach((tag) => {
            queryParams.append('except[]', tag);
        });
    }

    try {
        const { result = {}, pending } = await fetchData(`/api/v1/posts?${queryParams.toString()}`);
        posts.value = result.data;
        loading.value = pending;
        await nextTick();
        handleScroll();
    } catch (e) {
        error.value = e.message;
    } finally {
        loading.value = false;
    }
};

const handleScroll = () => {
    const container = sliderContainer.value;

    if (container) {
        isAtStart.value = container.scrollLeft === 0;
        isAtEnd.value = container.scrollLeft + container.clientWidth >= container.scrollWidth - 5;
    }
};

const scrollToPrev = () => {
    const container = sliderContainer.value;

    if (container) {
        const slideWidth = container.querySelector('div').clientWidth;
        container.scrollBy({ left: -(slideWidth + 16), behavior: 'smooth' });
    }
};

const scrollToNext = () => {
    const container = sliderContainer.value;

    if (container) {
        const slideWidth = container.querySelector('div').clientWidth;
        container.scrollBy({ left: slideWidth + 16, behavior: 'smooth' });
    }
};

onMounted(() => {
    fetchPosts();
    const container = sliderContainer.value;

    if (container) {
        container.addEventListener('scroll', handleScroll);
    }
});

watch(
    () => posts.value,
    () => {
        nextTick(() => {
            handleScroll();
        });
    }
);

watch(() => [props.limit, props.highlighted], fetchPosts, {
    deep: true
});
</script>

<template>
    <div class="relative">
        <div v-if="loading"
            class="flex space-x-4 overflow-x-auto snap-x snap-mandatory scroll-smooth scrolling-touch [&::-webkit-scrollbar]:hidden [scrollbar-width:none] lg:grid lg:gap-4 lg:space-x-0"
            :class="`lg:grid-cols-${props.limit}`">
            <div v-for="n in limit" :key="n" class="w-full flex-shrink-0 snap-center lg:w-auto">
                <HighlightItemSkeleton />
            </div>
        </div>

        <div v-else-if="error">
            <p>An error occurred while loading the posts.</p>
        </div>

        <div v-else>
            <div ref="sliderContainer"
                class="highlight-items flex space-x-4 overflow-x-auto snap-x snap-mandatory scroll-smooth scrolling-touch [&::-webkit-scrollbar]:hidden [scrollbar-width:none] lg:grid lg:gap-4 lg:space-x-0"
                :class="`lg:grid-cols-${props.limit}`" @scroll="handleScroll">
                <div v-for="post in posts" :key="post.id"
                    class="w-full flex-shrink-0 gap-5 snap-center lg:w-auto highlight-item">
                    <HighlightItem :key="post.id" :post="post" />
                </div>
            </div>
            <button :disabled="isAtStart"
                class="absolute top-1/2 -translate-y-1/2 left-0 bg-white/50 backdrop-blur-sm rounded-full p-2 text-gray-800 shadow-md lg:hidden disabled:opacity-50 disabled:cursor-not-allowed"
                @click="scrollToPrev">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                </svg>
            </button>
            <button :disabled="isAtEnd"
                class="absolute top-1/2 -translate-y-1/2 right-0 bg-white/50 backdrop-blur-sm rounded-full p-2 text-gray-800 shadow-md lg:hidden disabled:opacity-50 disabled:cursor-not-allowed"
                @click="scrollToNext">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
            </button>
        </div>
    </div>
</template>

<style scoped>
.highlight-items .highlight-item .highlight-item-overlay {
    right: unset;
    left: 0;
}
</style>
